// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, Paper, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/Key';
import BlockIcon from '@mui/icons-material/Block';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import api from '../utils/api';
import ChampRecherche from '../components/ChampRecherche';
import { filterItems } from '../utils/recherche';

/**
 * Comptes de pose : les comptes des poseurs, valables chez tous les clients.
 *
 * C'est le seul compte qui traverse l'isolation entre clients (il voit ce que
 * voit la direction de l'établissement visité, résidents compris) : la route
 * exige donc `users_manage`, et non `clients_write`, accordé par défaut à tout
 * compte d'équipe. `python -m app.tools.compte_de_pose` appelle la même
 * fonction du backend. L'adresse voyage dans le corps du POST, jamais dans
 * l'URL : nginx journalise `$request`.
 */

const BASE = '/comptes-de-pose';

function errorMessage(e, defaultValue) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (e?.response?.status === 403) {
    return "Votre compte n'a pas le droit de gérer les comptes (permission « users_manage »).";
  }
  return defaultValue;
}

export default function InstallAccountsPage() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState('');
  const [recherche, setRecherche] = useState('');
  const [enCours, setEnCours] = useState(null);   // id du compte en cours d'action
  const [creation, setCreation] = useState(null); // { email, nom, enCours, erreur }
  const [secret, setSecret] = useState(null);     // { email, password, titre }
  const [message, setMessage] = useState('');

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get(BASE);
      setAccounts(data || []);
      setError('');
    } catch (e) {
      setAccounts([]);
      setError(errorMessage(e, 'Comptes de pose indisponibles.'));
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const filters = useMemo(
    () => filterItems(accounts || [], recherche, (c) => [c.email, c.nom]),
    [accounts, recherche],
  );

  const createAccount = async () => {
    const email = (creation?.email || '').trim();
    if (!email) return;
    setCreation((c) => ({ ...c, enCours: true, erreur: '' }));
    try {
      const { data } = await api.post(BASE, { email, nom: (creation.nom || '').trim() || null });
      setCreation(null);
      setSecret({
        email: data.email,
        motDePasse: data.mot_de_passe_provisoire,
        titre: 'Compte de pose créé',
      });
      await charger();
    } catch (e) {
      setCreation((c) => ({
        ...c,
        enCours: false,
        erreur: errorMessage(e, 'Création impossible.'),
      }));
    }
  };

  const act = async (account, path, label) => {
    if (enCours) return;
    setEnCours(account.id);
    try {
      const { data } = await api.post(`${BASE}/${account.id}/${path}`);
      if (data?.mot_de_passe_provisoire) {
        setSecret({
          email: data.email,
          motDePasse: data.mot_de_passe_provisoire,
          titre: 'Nouveau mot de passe',
        });
      } else {
        setMessage(`${label} : ${account.email}`);
      }
      await charger();
    } catch (e) {
      setError(errorMessage(e, `${label} impossible.`));
    } finally {
      setEnCours(null);
    }
  };

  const copier = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage('Copié');
    } catch {
      // Presse-papiers refusé (contexte non sécurisé) : la valeur reste
      // affichée et sélectionnable, ce qui suffit.
      setMessage('Copie refusée par le navigateur — sélectionnez le texte.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Comptes de pose</Typography>
        <Tooltip title="Recharger">
          <IconButton onClick={charger}><RefreshIcon /></IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreation({ email: '', nom: '', enCours: false, erreur: '' })}
        >
          Nouveau compte
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Ces comptes valent chez tous les clients</AlertTitle>
        Un compte de pose peut entrer dans n’importe quel établissement et y voir ce
        que voit sa direction, résidents compris. Il n’en voit qu’un à la fois, et
        chaque entrée est inscrite au journal de l’établissement visité.
      </Alert>

      {!!error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2, maxWidth: 420 }}>
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Adresse ou nom"
          compte={filters.length}
          total={(accounts || []).length}
          nomObjet="compte"
        />
      </Box>

      {accounts === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Adresse</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>État</TableCell>
                <TableCell>Dernière connexion</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {recherche
                        ? `Aucun compte ne correspond à « ${recherche} ».`
                        : 'Aucun compte de pose. Le bouton « Nouveau compte » en fabrique un.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filters.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.email}</TableCell>
                  <TableCell>{c.nom || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.actif ? 'Actif' : 'Révoqué'}
                      color={c.actif ? 'success' : 'default'}
                      variant={c.actif ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {c.derniere_connexion
                      ? new Date(c.derniere_connexion).toLocaleString('fr-FR')
                      : 'Jamais'}
                  </TableCell>
                  <TableCell align="right">
                    {enCours === c.id ? <CircularProgress size={18} /> : (
                      <>
                        <Tooltip title="Nouveau mot de passe (l’ancien cesse de fonctionner)">
                          <span>
                            <IconButton size="small"
                                        onClick={() => act(c, 'mot-de-passe', 'Mot de passe renouvelé')}>
                              <KeyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {c.actif ? (
                          <Tooltip title="Révoquer : coupe immédiatement le téléphone du poseur">
                            <span>
                              <IconButton size="small" color="error"
                                          onClick={() => act(c, 'revocation', 'Compte révoqué')}>
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Remettre en service (le mot de passe ne change pas)">
                            <span>
                              <IconButton size="small"
                                          onClick={() => act(c, 'reactivation', 'Compte réactivé')}>
                                <RestartAltIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!creation} onClose={() => !creation?.enCours && setCreation(null)}
              fullWidth maxWidth="sm">
        <DialogTitle>Nouveau compte de pose</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth margin="dense" type="email" label="Adresse e-mail"
            value={creation?.email || ''}
            onChange={(e) => setCreation((c) => ({ ...c, email: e.target.value }))}
            disabled={creation?.enCours}
          />
          <TextField
            fullWidth margin="dense" label="Nom affiché"
            helperText="C’est ce nom que le client lira dans son journal quand ce compte entrera chez lui."
            value={creation?.nom || ''}
            onChange={(e) => setCreation((c) => ({ ...c, nom: e.target.value }))}
            disabled={creation?.enCours}
          />
          {!!creation?.erreur && <Alert severity="error" sx={{ mt: 1 }}>{creation.erreur}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreation(null)} disabled={creation?.enCours}>Annuler</Button>
          <Button variant="contained" onClick={createAccount}
                  disabled={creation?.enCours || !(creation?.email || '').trim()}>
            {creation?.enCours ? 'Création…' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Le secret ne s'affiche qu'ici, une seule fois. Ni Échap ni clic
          extérieur ne ferment la boîte : on la ferme volontairement. */}
      <Dialog open={!!secret} disableEscapeKeyDown fullWidth maxWidth="sm">
        <DialogTitle>{secret?.titre}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Ce mot de passe ne sera plus affiché. Transmettez-le au poseur ; l’application
            lui demandera d’en choisir un autre à sa première connexion.
          </DialogContentText>
          <Typography variant="body2" color="text.secondary">{secret?.email}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography
              component="code"
              sx={{ fontFamily: 'monospace', fontSize: '1.1rem', userSelect: 'all' }}
            >
              {secret?.motDePasse}
            </Typography>
            <Tooltip title="Copier">
              <IconButton size="small" onClick={() => copier(secret.motDePasse)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSecret(null)}>
            J’ai noté le mot de passe
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage('')}
        message={message}
      />
    </Box>
  );
}
